import React, { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Option } from 'react-dropdown';
import { Equipment, ISiteEquipment, PropertyState } from 'shared/api/api';
import { localized } from 'shared/state/i18n/i18n';
import { FlexColumn } from '../basic-flex/flex-column';
import { FlexRow } from '../basic-flex/flex-row';
import { ShowConfirmAlert, ShowOkAlert } from '../skiold-alert/skiold-alert';
import { SkioldButton } from '../skiold-components/skiold-button/skiold-button';
import { SkioldFormDropdown } from '../skiold-components/skiold-dropdown/skiold-form-dropdown';
import { SkioldFormsWrapper } from '../skiold-components/skiold-forms-wrapper/skiold-forms-wrapper';
import { FormRow } from '../skiold-components/skiold-forms-wrapper/skiold-forms-wrapper-types';
import { SkioldFormInput } from '../skiold-components/skiold-input/skiold-form-input';
import { useEditContext, useEquipmentsContext, useProcessEquipmentContext } from './create-site-context-provider';
import { generateTimeZoneOptions } from './create-site-helpers';
import { EquipmentInformation } from './equipment-information/equipment-information';
import { useGetDeviceProxyProperties } from './equipment-information/use-get-device-proxy-properties';
import { FormTitle } from './form-title';
import './process-equipment-form.scss';
import { equipmentPrefix } from './equipment-information/equipment-information-helpers';

interface Props {
	onCancel: () => void;
}

export const ProcessEquipmentForm: FC<Props> = memo(({ onCancel }) => {
	const { fields, setField, clearFields } = useProcessEquipmentContext();
	const { equipments, addEquipment, updateEquipment, markEquipmentForDeletion } = useEquipmentsContext();
	const { editingProcessEquipment } = useEditContext();

	// The process equipment which is currently edited if any
	const activeProcessEquipment = useMemo(
		() => equipments.find(eq => eq.code === editingProcessEquipment),
		[editingProcessEquipment, equipments],
	);

	const deviceProxy = useGetDeviceProxyProperties(activeProcessEquipment);
	const initializedForRef = useRef<string | undefined>(undefined);

	const isValidInput = useCallback((fields: Partial<ISiteEquipment>) => {
		const missingInputs: string[] = [];

		if (!fields.ipAddress) missingInputs.push(localized('IpAddress'));
		if (!fields.code) missingInputs.push(localized('Code'));
		if (!fields.timeZone) missingInputs.push(localized('TimeZone'));
		if (!fields.equipment) missingInputs.push(localized('processEquipment'));

		if (missingInputs.length > 0) {
			ShowOkAlert(`${localized('FillOut')}:\n- ${missingInputs.join('\n- ')}`);
			return false;
		}

		return true;
	}, []);

	// Pre-populate ipAddress and timezone from device proxy only if the fields are not already
	// set — startEditingProcessEquipment pre-fills from local state, so only fall back to Azure
	// values when those fields are genuinely empty (e.g. the initial site load failed).
	useEffect(() => {
		if (!deviceProxy?.properties || !activeProcessEquipment) return;
		if (initializedForRef.current === activeProcessEquipment.code) return;

		initializedForRef.current = activeProcessEquipment.code;

		const ipAddressKey = `${equipmentPrefix(activeProcessEquipment.equipment)}EndpointAddress`;
		const findDesired = (name: string) =>
			deviceProxy.properties!.find(p => p.propertyName === name && p.state === PropertyState.Desired)?.value;

		const findReported = (name: string) =>
			deviceProxy.properties!.find(p => p.propertyName === name && p.state === PropertyState.Reported)?.value ??
			'';

		if (!fields.ipAddress) setField('ipAddress', findDesired(ipAddressKey) ?? findReported(ipAddressKey));
		if (!fields.timeZone) setField('timeZone', findDesired('timeZone') ?? findReported('timeZone'));
	}, [activeProcessEquipment, deviceProxy?.properties, fields.ipAddress, fields.timeZone, setField]);

	const handleCancelPressed = useCallback(() => {
		clearFields();
		onCancel();
	}, [clearFields, onCancel]);

	const equipmentOption = useMemo(() => {
		return Object.values(Equipment).map(value => ({
			label: value,
			value: value,
		}));
	}, []);

	const timeZoneOption = useMemo(() => {
		return generateTimeZoneOptions();
	}, []);

	const onIpAddressChanged = useCallback((text: string) => setField('ipAddress', text), [setField]);
	const onLocationCodeChanged = useCallback((text: string) => setField('code', text), [setField]);
	const onTimezoneChanged = useCallback((option: Option) => setField('timeZone', option.value), [setField]);
	const selectEquipment = useCallback(
		(option: Option) => {
			setField('equipment', option.value as Equipment);
		},
		[setField],
	);

	const handleDeletePressed = useCallback(async () => {
		if (!activeProcessEquipment) return;
		const confirmed = await ShowConfirmAlert(localized('PromptSureDelete'));
		if (!confirmed) return;
		markEquipmentForDeletion(activeProcessEquipment);
		clearFields();
		onCancel();
	}, [activeProcessEquipment, clearFields, markEquipmentForDeletion, onCancel]);

	const handleUpdatePressed = useCallback(() => {
		if (!isValidInput(fields)) return;

		updateEquipment(fields as ISiteEquipment);
		clearFields();
		onCancel();
	}, [clearFields, fields, isValidInput, onCancel, updateEquipment]);

	const handleAddPressed = useCallback(() => {
		if (!isValidInput(fields)) return;

		addEquipment(fields as ISiteEquipment);
		clearFields();
		onCancel();
	}, [isValidInput, fields, addEquipment, clearFields, onCancel]);

	const formRows: Array<FormRow> = useMemo(() => {
		return new Array<FormRow>(
			{
				name: localized('IpAddress'),
				component: <SkioldFormInput text={fields.ipAddress} onChangeText={onIpAddressChanged} />,
			},
			{
				name: localized('Code'),
				// Code is the device id and is the key we match on for update/delete, so it must
				// not change once the equipment exists.
				component: (
					<SkioldFormInput
						text={fields.code}
						onChangeText={onLocationCodeChanged}
						editable={!activeProcessEquipment}
					/>
				),
			},
			{
				name: localized('TimeZone'),
				component: (
					<SkioldFormDropdown
						onValueChanged={onTimezoneChanged}
						items={timeZoneOption}
						selectedValue={timeZoneOption.find(o => o.value === fields.timeZone)}
					/>
				),
			},
			{
				name: localized('processEquipment'),
				component: (
					<SkioldFormDropdown
						onValueChanged={selectEquipment}
						items={equipmentOption}
						selectedValue={equipmentOption.find(o => o.value === fields.equipment)}
					/>
				),
			},
		);
	}, [
		fields.ipAddress,
		fields.code,
		fields.timeZone,
		fields.equipment,
		activeProcessEquipment,
		onIpAddressChanged,
		onLocationCodeChanged,
		onTimezoneChanged,
		timeZoneOption,
		selectEquipment,
		equipmentOption,
	]);

	return (
		<FlexColumn className="process-equipment-form">
			<FormTitle title={localized('processEquipment')} />
			<SkioldFormsWrapper formRows={formRows} containerClassName="process-equipment-form-wrapper" />
			<FlexRow sx={{ justifyContent: 'flex-end' }}>
				{activeProcessEquipment ? (
					<>
						<SkioldButton title={localized('Delete')} onPress={handleDeletePressed} />
						<SkioldButton title={localized('Update')} onPress={handleUpdatePressed} />
					</>
				) : (
					<SkioldButton title={localized('Add')} onPress={handleAddPressed} />
				)}
				<SkioldButton title={localized('Cancel')} onPress={handleCancelPressed} />
			</FlexRow>
			{activeProcessEquipment && <EquipmentInformation equipment={activeProcessEquipment} />}
		</FlexColumn>
	);
});
